import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { obdApi } from '../api/obd.api';
import { companyApi } from '../api/company.api';
import { OBDDevice, OBDDeviceType, DeviceConfig } from '../types/obd';
import { Company } from '../types/user';
import { toast } from '../utils/toast';

interface DeviceFormData {
  device_id: string;
  device_type: OBDDeviceType;
}

const statusColors: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  assigned: 'bg-blue-100 text-blue-800',
};

export const DeviceInventoryPage: React.FC = () => {
  const { t } = useTranslation();

  const [devices, setDevices] = useState<OBDDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'available' | 'assigned' | ''>('');

  // Edit modal state
  const [editingDevice, setEditingDevice] = useState<OBDDevice | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState<DeviceFormData>({ device_id: '', device_type: 'FMC003' });
  const [editFormErrors, setEditFormErrors] = useState<Partial<DeviceFormData>>({});

  // Allocate modal state
  const [allocatingDevice, setAllocatingDevice] = useState<OBDDevice | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [isAllocating, setIsAllocating] = useState(false);

  // Config modal state
  const [configDevice, setConfigDevice] = useState<OBDDevice | null>(null);
  const [deviceConfig, setDeviceConfig] = useState<DeviceConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Add modal form state
  const [formData, setFormData] = useState<DeviceFormData>({
    device_id: '',
    device_type: 'FMC003',
  });

  const [formErrors, setFormErrors] = useState<Partial<DeviceFormData>>({});

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setIsLoading(true);
      const data = await obdApi.getDeviceInventory();
      setDevices(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to load device inventory:', error);
      toast.error(error.response?.data?.message || t('devices.failedLoad'));
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Add form ──────────────────────────────────────────────────────────────

  const validateForm = (data: DeviceFormData, skipDeviceId = false): Partial<DeviceFormData> => {
    const errors: Partial<DeviceFormData> = {};

    if (!skipDeviceId) {
      if (!data.device_id.trim()) {
        errors.device_id = t('devices.validation.deviceIdRequired');
      } else if (!/^\d{15}$/.test(data.device_id.trim())) {
        errors.device_id = t('devices.validation.deviceIdInvalid');
      }
    }

    if (!data.device_type) {
      errors.device_type = t('devices.validation.deviceTypeRequired');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsSubmitting(true);
      await obdApi.createDevice(formData.device_id.trim(), formData.device_type);
      toast.success(t('devices.addModal.createdSuccess'));

      setIsAddModalOpen(false);
      setFormData({ device_id: '', device_type: 'FMC003' });
      setFormErrors({});
      await loadDevices();
    } catch (error: any) {
      console.error('Failed to create device:', error);
      toast.error(error.response?.data?.message || t('devices.addModal.failedCreate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof DeviceFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // ── Edit form ─────────────────────────────────────────────────────────────

  const openEditModal = (device: OBDDevice) => {
    setEditingDevice(device);
    setEditFormData({ device_id: device.device_id, device_type: device.device_type });
    setEditFormErrors({});
  };

  const closeEditModal = () => {
    if (isEditSubmitting) return;
    setEditingDevice(null);
    setEditFormErrors({});
  };

  const handleEditInputChange = (field: keyof DeviceFormData, value: string) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDevice) return;

    const isAssigned = editingDevice.status === 'assigned';
    const errors = validateForm(editFormData, isAssigned);
    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      return;
    }

    const updates: { device_id?: string; device_type?: OBDDeviceType } = {
      device_type: editFormData.device_type,
    };
    if (!isAssigned) {
      updates.device_id = editFormData.device_id.trim();
    }

    try {
      setIsEditSubmitting(true);
      await obdApi.updateInventoryDevice(editingDevice.device_id, updates);
      toast.success(t('devices.editModal.updatedSuccess'));
      setEditingDevice(null);
      await loadDevices();
    } catch (error: any) {
      console.error('Failed to update device:', error);
      toast.error(error.response?.data?.message || t('devices.editModal.failedUpdate'));
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (device: OBDDevice) => {
    if (!window.confirm(t('devices.deleteConfirm', { id: device.device_id }))) return;

    try {
      await obdApi.deleteInventoryDevice(device.device_id);
      toast.success(t('devices.deleteSuccess'));
      await loadDevices();
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 409) {
        toast.error(t('devices.deleteAssignedError'));
      } else {
        toast.error(error.response?.data?.message || t('devices.deleteFailed'));
      }
    }
  };

  // ── Allocate ──────────────────────────────────────────────────────────────

  const openAllocateModal = async (device: OBDDevice) => {
    setAllocatingDevice(device);
    setSelectedCompanyId(device.company_id?._id ?? '');
    setIsLoadingCompanies(true);
    try {
      const response = await companyApi.listCompanies();
      const activeCompanies = response.data.filter((c) => c.status === 'active');
      setCompanies(activeCompanies);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const closeAllocateModal = () => {
    if (isAllocating) return;
    setAllocatingDevice(null);
    setSelectedCompanyId('');
    setCompanies([]);
  };

  const handleAllocate = async () => {
    if (!allocatingDevice || !selectedCompanyId) return;
    try {
      setIsAllocating(true);
      await obdApi.allocateDevice(allocatingDevice.device_id, selectedCompanyId);
      toast.success(t('devices.allocateModal.success'));
      closeAllocateModal();
      await loadDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('devices.allocateModal.failed'));
    } finally {
      setIsAllocating(false);
    }
  };

  // ── Config modal ──────────────────────────────────────────────────────────

  const openConfigModal = async (device: OBDDevice) => {
    setConfigDevice(device);
    setDeviceConfig(null);
    setConfigError(null);
    setIsApiKeyVisible(false);
    setIsLoadingConfig(true);
    try {
      const config = await obdApi.getDeviceConfig(device.device_id);
      setDeviceConfig(config);
    } catch (error: any) {
      setConfigError(error.response?.data?.message || t('devices.configModal.failedLoad'));
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const closeConfigModal = () => {
    if (isRegenerating) return;
    setConfigDevice(null);
    setDeviceConfig(null);
    setConfigError(null);
    setIsApiKeyVisible(false);
  };

  const handleRegenerateKey = async () => {
    if (!configDevice || !window.confirm(t('devices.configModal.regenerateConfirm'))) return;
    try {
      setIsRegenerating(true);
      const newConfig = await obdApi.regenerateApiKey(configDevice.device_id);
      setDeviceConfig(newConfig);
      setIsApiKeyVisible(true);
      toast.success(t('devices.configModal.regenerateSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('devices.configModal.regenerateFailed'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('common.copied'));
    } catch {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('common.copied'));
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const filteredDevices = devices.filter((device) => {
    const matchesSearch =
      !searchQuery ||
      device.device_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.device_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (device.assigned_vehicle_plate &&
        device.assigned_vehicle_plate.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = !statusFilter || device.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const availableCount = devices.filter((d) => d.status === 'available').length;
  const assignedCount = devices.filter((d) => d.status === 'assigned').length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('devices.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">{t('devices.subtitle')}</p>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)} size="lg">
            {t('devices.addDevice')}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <input
                type="search"
                placeholder={t('devices.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'available' | 'assigned' | '')}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">{t('devices.allStatuses')}</option>
                <option value="available">{t('devices.statusAvailable')}</option>
                <option value="assigned">{t('devices.statusAssigned')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Devices Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
              <p className="mt-4 text-gray-600">{t('devices.loading')}</p>
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto mb-4 flex items-center justify-center h-16 w-16 rounded-full bg-gray-100">
                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery || statusFilter
                  ? t('devices.noDevicesFound')
                  : t('devices.noDevicesYet')}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || statusFilter
                  ? t('devices.tryFilters')
                  : t('devices.getStarted')}
              </p>
              {!searchQuery && !statusFilter && (
                <Button onClick={() => setIsAddModalOpen(true)}>
                  {t('devices.addFirstDevice')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.deviceId')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.deviceType')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.company')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.assignedVehicle')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.created')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('devices.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDevices.map((device) => (
                    <tr key={device._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-md flex items-center justify-center">
                            <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                            </svg>
                          </div>
                          <span className="text-sm font-mono font-medium text-gray-900">
                            {device.device_id}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {device.device_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            statusColors[device.status]
                          }`}
                        >
                          {device.status === 'available'
                            ? t('devices.statusAvailable')
                            : t('devices.statusAssigned')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {device.company_id ? device.company_id.name : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {device.assigned_vehicle_plate || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(device.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Allocate / Reallocate button */}
                          <button
                            type="button"
                            onClick={() => openAllocateModal(device)}
                            title={
                              device.company_id
                                ? t('devices.reallocateButton')
                                : t('devices.allocateButton')
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-purple-200 bg-white text-purple-600 text-xs font-medium hover:bg-purple-50 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors whitespace-nowrap"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {device.company_id
                              ? t('devices.reallocateButton')
                              : t('devices.allocateButton')}
                          </button>

                          {/* Config button */}
                          <button
                            type="button"
                            onClick={() => openConfigModal(device)}
                            title={t('devices.configButton')}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>

                          {/* Edit button */}
                          <button
                            type="button"
                            onClick={() => openEditModal(device)}
                            title={t('devices.editButton')}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => handleDelete(device)}
                            title={t('devices.deleteButton')}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-red-200 bg-white text-red-500 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        {!isLoading && devices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('devices.stats.total')}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{devices.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('devices.stats.available')}</div>
              <div className="text-2xl font-bold text-green-600 mt-1">{availableCount}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('devices.stats.assigned')}</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">{assignedCount}</div>
            </div>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !isSubmitting && setIsAddModalOpen(false)}
        title={t('devices.addModal.title')}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('devices.addModal.deviceId')}
            placeholder={t('devices.addModal.deviceIdPlaceholder')}
            value={formData.device_id}
            onChange={(e) => handleInputChange('device_id', e.target.value)}
            error={formErrors.device_id}
            helperText={t('devices.addModal.deviceIdHelper')}
            disabled={isSubmitting}
            autoFocus
            maxLength={15}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('devices.addModal.deviceType')}
            </label>
            <select
              value={formData.device_type}
              onChange={(e) =>
                handleInputChange('device_type', e.target.value as OBDDeviceType)
              }
              disabled={isSubmitting}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="FMC003">Teltonika FMC003</option>
              <option value="FMB003">Teltonika FMB003</option>
              <option value="FMB010">Teltonika FMB010</option>
            </select>
            {formErrors.device_type && (
              <p className="mt-1 text-sm text-red-600">{formErrors.device_type}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('devices.addModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? t('devices.addModal.creating') : t('devices.addModal.create')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Allocate Device Modal */}
      <Modal
        isOpen={!!allocatingDevice}
        onClose={closeAllocateModal}
        title={t('devices.allocateModal.title')}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('devices.allocateModal.selectCompany')}
            </label>
            {isLoadingCompanies ? (
              <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
                {t('common.loading')}
              </div>
            ) : (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={isAllocating}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">— {t('devices.allocateModal.selectCompany')} —</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={closeAllocateModal}
              disabled={isAllocating}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleAllocate}
              isLoading={isAllocating}
              disabled={!selectedCompanyId || isAllocating}
              className="flex-1"
            >
              {t('devices.allocateModal.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Device Configuration Modal */}
      <Modal
        isOpen={!!configDevice}
        onClose={closeConfigModal}
        title={
          configDevice
            ? `${t('devices.configModal.title')} — ${configDevice.device_id.slice(-5)}`
            : t('devices.configModal.title')
        }
        size="lg"
      >
        <div className="space-y-4">
          {isLoadingConfig && (
            <div className="flex items-center gap-3 py-8 justify-center text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
              {t('devices.configModal.loading')}
            </div>
          )}

          {configError && !isLoadingConfig && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {configError}
            </div>
          )}

          {deviceConfig && !isLoadingConfig && (
            <>
              {/* Section 1 — Device Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('devices.configModal.deviceInfo')}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-600 shrink-0">{t('devices.configModal.deviceId')}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm text-gray-900 truncate">{deviceConfig.device_id}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(deviceConfig.device_id)}
                        className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-600 shrink-0">{t('devices.configModal.deviceType')}</span>
                    <span className="font-mono text-sm text-gray-900">{deviceConfig.device_type}</span>
                  </div>
                </div>
              </div>

              {/* Section 2 — API Key */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('devices.configModal.apiKey')}
                </h3>
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3">
                  <svg className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-xs text-amber-700">{t('devices.configModal.apiKeyWarning')}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-sm text-gray-900 truncate min-w-0">
                    {isApiKeyVisible
                      ? deviceConfig.api_key
                      : deviceConfig.api_key.slice(0, 4) + '••••••••••••••••••••'}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsApiKeyVisible((v) => !v)}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      {isApiKeyVisible ? t('devices.configModal.hideKey') : t('devices.configModal.showKey')}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(deviceConfig.api_key)}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={handleRegenerateKey}
                      disabled={isRegenerating}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-red-300 bg-white text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {isRegenerating ? (
                        <span className="inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-red-500" />
                      ) : (
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                      {t('devices.configModal.regenerate')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Section 3 — Teltonika Configurator Settings */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                {/* Step 1 — Server Settings */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('devices.configModal.serverSettings')}
                  </h3>
                  {(() => {
                    let domain = deviceConfig.config.server_url;
                    try { domain = new URL(deviceConfig.config.server_url).hostname; } catch { /* keep original */ }
                    return (
                      <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                        <span className="text-gray-500">{t('devices.configModal.protocol')}</span>
                        <span className="font-mono text-gray-900">HTTPS</span>

                        <span className="text-gray-500">{t('devices.configModal.domain')}</span>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-gray-900 truncate">{domain}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(domain)}
                            className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                          >
                            Copy
                          </button>
                        </div>

                        <span className="text-gray-500">{t('devices.configModal.port')}</span>
                        <span className="font-mono text-gray-900">443</span>

                        <span className="text-gray-500">{t('devices.configModal.path')}</span>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-gray-900 truncate">{deviceConfig.config.endpoint}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(deviceConfig.config.endpoint)}
                            className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                          >
                            Copy
                          </button>
                        </div>

                        <span className="text-gray-500">{t('devices.configModal.method')}</span>
                        <span className="font-mono text-gray-900">{deviceConfig.config.method}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Step 2 — Auth Header */}
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('devices.configModal.authHeader')}
                  </h3>
                  <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                    <span className="text-gray-500">{t('devices.configModal.headerName')}</span>
                    <span className="font-mono text-gray-900">{deviceConfig.config.auth_header}</span>

                    <span className="text-gray-500">{t('devices.configModal.headerValue')}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-gray-900 truncate">
                        {isApiKeyVisible
                          ? deviceConfig.api_key
                          : deviceConfig.api_key.slice(0, 4) + '••••••••'}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(deviceConfig.api_key)}
                        className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 3 — Trip Detection */}
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('devices.configModal.tripDetection')}
                  </h3>
                  <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                    <span className="text-gray-500">{t('devices.configModal.detectionMethod')}</span>
                    <span className="font-mono text-gray-900">{t('devices.configModal.detectionMethodValue')}</span>

                    <span className="text-gray-500">{t('devices.configModal.tripEndDelay')}</span>
                    <span className="font-mono text-gray-900">
                      {deviceConfig.config.trip_end_delay_seconds} {t('devices.configModal.seconds')}
                    </span>

                    <span className="text-gray-500">{t('devices.configModal.minDistance')}</span>
                    <span className="font-mono text-gray-900">
                      {deviceConfig.config.min_distance_meters} {t('devices.configModal.meters')}
                    </span>
                  </div>
                </div>

                {/* Step 4 — AVL IO Elements */}
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('devices.configModal.avlElements')}
                  </h3>
                  {/* Group IOs by category */}
                  {(['required', 'maintenance', 'oem'] as const).map((group) => {
                    const groupIds = deviceConfig.config.avl_ids.filter((a) => a.group === group);
                    if (!groupIds.length) return null;
                    const groupLabel: Record<string, string> = {
                      required:    'Requis — Détection de trajet',
                      maintenance: 'Santé véhicule — Tableau Maintenance',
                      oem:         'OEM — Nécessite Codec 8E + mode OEM activé',
                    };
                    const groupColor: Record<string, string> = {
                      required:    'bg-primary-50 text-primary-700',
                      maintenance: 'bg-green-50 text-green-700',
                      oem:         'bg-purple-50 text-purple-700',
                    };
                    return (
                      <div key={group} className="space-y-1">
                        <div className={`text-xs font-semibold px-2 py-1 rounded ${groupColor[group]}`}>
                          {groupLabel[group]}
                        </div>
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-400">
                              <th className="pb-1 pr-3 font-medium w-12">ID</th>
                              <th className="pb-1 pr-3 font-medium">Nom</th>
                              <th className="pb-1 pr-3 font-medium">Priorité</th>
                              <th className="pb-1 font-medium text-gray-400">Note</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {groupIds.map((avl) => (
                              <tr key={avl.id}>
                                <td className="py-1.5 pr-3 font-mono font-bold text-gray-900">{avl.id}</td>
                                <td className="py-1.5 pr-3 text-gray-700">{avl.name}</td>
                                <td className="py-1.5 pr-3">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      avl.priority === 'high'
                                        ? 'bg-red-100 text-red-700'
                                        : avl.priority === 'medium'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {avl.priority === 'high' ? 'Haute' : avl.priority === 'medium' ? 'Moyenne' : 'Basse'}
                                  </span>
                                </td>
                                <td className="py-1.5 text-xs text-gray-400 italic">{avl.note}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 4 — Important Notes */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                  {t('devices.configModal.notes')}
                </h3>
                <ul className="space-y-1.5 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                    {t('devices.configModal.note1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                    {t('devices.configModal.note2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                    {t('devices.configModal.note3')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                    {t('devices.configModal.note4')}
                  </li>
                </ul>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-2">
            <Button type="button" variant="secondary" onClick={closeConfigModal}>
              {t('devices.configModal.close')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Device Modal */}
      <Modal
        isOpen={!!editingDevice}
        onClose={closeEditModal}
        title={t('devices.editModal.title')}
        size="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <Input
              label={t('devices.addModal.deviceId')}
              placeholder={t('devices.addModal.deviceIdPlaceholder')}
              value={editFormData.device_id}
              onChange={(e) => handleEditInputChange('device_id', e.target.value)}
              error={editFormErrors.device_id}
              helperText={
                editingDevice?.status === 'assigned'
                  ? t('devices.editModal.imeiLockedHint')
                  : t('devices.addModal.deviceIdHelper')
              }
              disabled={isEditSubmitting || editingDevice?.status === 'assigned'}
              maxLength={15}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('devices.addModal.deviceType')}
            </label>
            <select
              value={editFormData.device_type}
              onChange={(e) =>
                handleEditInputChange('device_type', e.target.value as OBDDeviceType)
              }
              disabled={isEditSubmitting}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="FMC003">Teltonika FMC003</option>
              <option value="FMB003">Teltonika FMB003</option>
              <option value="FMB010">Teltonika FMB010</option>
            </select>
            {editFormErrors.device_type && (
              <p className="mt-1 text-sm text-red-600">{editFormErrors.device_type}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={closeEditModal}
              disabled={isEditSubmitting}
              className="flex-1"
            >
              {t('devices.addModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isEditSubmitting} className="flex-1">
              {isEditSubmitting ? t('devices.editModal.saving') : t('devices.editModal.save')}
            </Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};
